import { omitBy, range, sample } from 'lodash';

import { JsonSchemaArray, JsonSchemaBase, JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
import { isArray, isEnum } from '@evidentid/json-schema/schemaChecks';
import { CustomProperty, CustomPropertyType } from '@evidentid/tprm-portal-lib/models/dashboard';

import {
    sampleBooleans,
    sampleContactNames,
    sampleIntegers,
    sampleNumbers,
    sampleStrings,
    sampleUuid,
    specialTypeSamplePool,
} from '@/modules/entity-management/utils/csv-sample/constants';
import {
    getItemType, getOptionalData, rollRandomOptional,
} from '@/modules/entity-management/utils/csv-sample/helpers/common';
import { SampleInsured, SampleValues } from '@/modules/entity-management/utils/csv-sample/models';
import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
import { isEquipmentCustomProperty } from '@/utils/isEquipmentCustomProperty';

const arrayMaxLength = 5;

function generateSampleEmails(domain: string): string[] {
    return sampleContactNames.map((contact) => `${contact.emailPrefix}${domain}`);
}

function generateSampleArray(itemType: string, primitiveTypes: Record<string, string[]>): string {
    return [ ...range(1, Math.random() * arrayMaxLength + 1) ]
        .map(() => sample(primitiveTypes[itemType] || []))
        .join('|');
}

function generateSampleCollaterals(): string {
    const sampling = () => JSON.stringify(
        omitBy({
            description: sample(specialTypeSamplePool.collaterals.description),
            category: sample(specialTypeSamplePool.collaterals.category),
            limitRequired: sample(specialTypeSamplePool.collaterals.limitRequired),
            uniqueIdentifier: getOptionalData(sample(specialTypeSamplePool.collaterals.uniqueIdentifier)),
            maximumDeductible: getOptionalData(sample(specialTypeSamplePool.collaterals.maximumDeductible)),
        }, (v) => v == null || v === ''),
    );
    const maxSampleSize = 3;
    return [ ...range(1, Math.random() * maxSampleSize + 1) ]
        .map(sampling)
        .join('|');
}

function generateSampleEquipment(): string {
    const sampling = () => JSON.stringify(
        omitBy({
            description: sample(specialTypeSamplePool.equipment.description),
            equipmentValue: sample(specialTypeSamplePool.equipment.equipmentValue),
            rentalId: getOptionalData(sample(specialTypeSamplePool.equipment.rentalId)),
            rentalCompleteDate: getOptionalData(sample(specialTypeSamplePool.equipment.rentalCompleteDate)),
            rentalProtectionPlan: rollRandomOptional()
                ? sample(specialTypeSamplePool.equipment.rentalProtectionPlan)
                : undefined,
        }, (v) => v == null || v === ''),
    );
    const maxSampleSize = 3;
    return [ ...range(1, Math.random() * maxSampleSize + 1) ]
        .map(sampling)
        .join('|');
}

function generateSampleAddress(): string {
    const sampleAddress = sample(specialTypeSamplePool.address);
    return JSON.stringify(
        omitBy(sampleAddress, (v) => v == null || v === ''),
    );
}

const credentialTypesMaxSampleSize = 3;

function generateSampleCredentialTypes(field: CustomProperty): string | null {
    const labels = ((field.schema as JsonSchemaArray<any>).items as JsonSchemaEnum)?.enumLabels;
    if (!labels || labels.length === 0) {
        return null;
    }
    return [ ...range(1, Math.random() * credentialTypesMaxSampleSize + 1) ]
        .map(() => sample(labels))
        .join('|');
}

export function generateSampleValue(field: CustomProperty, values: SampleValues): string {
    if (isCollateralCustomProperty(field)) {
        return getOptionalData(values.specialTypes.collaterals || '') as string;
    } else if (isEquipmentCustomProperty(field)) {
        // Generated per property rather than shared across the row: an RP with two Equipment
        // properties would otherwise get byte-identical rows in both columns of the template.
        return getOptionalData(generateSampleEquipment()) as string;
    } else if (isEnum(field.schema)) {
        return sample(field.schema.enum) ?? '';
    } else if (field.type === CustomPropertyType.credentialTypes) {
        const sampled = generateSampleCredentialTypes(field);
        if (sampled !== null) {
            return sampled;
        }
        return sample(values.arrays.uuid) || '';
    } else {
        return sample(isArray(field.schema)
            ? values.arrays[getItemType(field.schema)]
            : values.primitives[(field.schema as JsonSchemaBase).type] || [],
        ) || '';
    }
}

export function generateSampleValues(insured: SampleInsured): SampleValues {
    // as of right now this function does not need to be recursive, as insured field types are statically limited
    const samplePrimitives = {
        string: sampleStrings,
        uuid: sampleUuid,
        integer: sampleIntegers,
        number: sampleNumbers,
        boolean: sampleBooleans,
        email: generateSampleEmails(insured.domain || 'example.com'),
    };

    const sampleArrays = {
        string: range(0, 5).map(() => generateSampleArray('string', samplePrimitives)),
        uuid: range(0, 5).map(() => generateSampleArray('uuid', samplePrimitives)),
        email: range(0, 5).map(() => generateSampleArray('email', samplePrimitives)),
    };

    return {
        primitives: samplePrimitives,
        arrays: sampleArrays,
        specialTypes: {
            collaterals: generateSampleCollaterals(),
            address: generateSampleAddress(),
        },
    };
}
